import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { format } from "date-fns";
import { api, Account, ActivityCode, Project, TimeEntry } from "./api";
import { useAuth } from "./authContext";
import { useToast } from "@/hooks/use-toast";
import {
  TIMER_STORAGE_KEY,
  TIMER_STOP_SIGNAL_KEY,
  timerSyncAction,
  shouldSkipEcho,
  type PersistedTimerState,
  type RemoteMarker,
} from "./timerSync";
import {
  buildHeldTimer,
  canHoldTimer,
  hasPersistableTimer,
  type HeldTimer,
} from "./timerHold";

interface TimerContextType {
  // Sheet
  isSheetOpen: boolean;
  openSheet: (date?: string) => void;
  openSheetForEdit: (entry: TimeEntry, accounts: Account[]) => void;
  openSheetWithContext: (accountId: string, projectId: string) => void;
  closeSheet: () => void;

  // Timer state (read-only for consumers)
  timerRunning: boolean;
  timerPaused: boolean;
  elapsedSeconds: number;
  handleStartTimer: () => void;
  handlePauseTimer: () => void;
  handleResumeTimer: () => void;
  handleStopTimer: () => void;
  handleResetTimer: () => void;
  formatTimer: (s: number) => string;
  // v1.5.90: a stopped timer waits for Save/Discard; one timer can be put on hold.
  timerStopped: boolean;
  heldTimer: HeldTimer | null;
  canHold: boolean;
  handleHoldAndStartNew: () => void;
  handleDiscardStopped: () => void;

  // Form state
  entryDate: string;
  setEntryDate: (v: string) => void;
  selectedAccount: string;
  setSelectedAccount: (v: string) => void;
  handleAccountChange: (v: string) => void;
  selectedProject: string;
  setSelectedProject: (v: string) => void;
  selectedCode: string;
  handleCodeChange: (v: string) => void;
  duration: number;
  setDuration: (v: number) => void;
  startTime: string;
  setStartTime: (v: string) => void;
  endTime: string;
  setEndTime: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;
  isBillable: boolean;
  setIsBillable: (v: boolean) => void;
  editingId: string | null;

  // Reference data
  accounts: Account[];
  activityCodes: ActivityCode[];
  projects: Project[];
  includeClosedProjects: boolean;
  setIncludeClosedProjects: (v: boolean) => void;

  // Quick-add project modal
  showAddProjectModal: boolean;
  setShowAddProjectModal: (v: boolean) => void;
  newProjectTitle: string;
  setNewProjectTitle: (v: string) => void;
  newProjectDescription: string;
  setNewProjectDescription: (v: string) => void;
  handleQuickAddProject: () => Promise<void>;
  savingProject: boolean;

  // Submit
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleCancelEdit: () => void;
  saving: boolean;

  // Notification for Today.tsx to reload
  lastSavedAt: number | null;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

// Notes are required on every admin-category code (900-level except PTO/holiday),
// so firm time always says what it was. 950 Staff meeting is exempt: the code is the note.
// Also required on the review and training codes (305/310/315/920): the note is the deliverable.
// Falls back to the old fixed list until codes load.
const ALWAYS_NOTE = new Set(["305", "310", "315", "920"]);
export function notesRequiredFor(code: string, codes: ActivityCode[]): boolean {
  if (code === "950") return false;
  if (ALWAYS_NOTE.has(code)) return true;
  const ac = codes.find((c) => c.code === code);
  return ac ? ac.category === "admin" : ["901", "905", "906", "908"].includes(code);
}

export function TimerProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();

  const todayStr = format(new Date(), "yyyy-MM-dd");

  // ─── Sheet ──────────────────────────────────────────────────────────────────
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [entryDate, setEntryDate] = useState(todayStr);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Saved timer form state — restored after finishing an edit while timer runs
  const preEditStateRef = useRef<{
    account: string; project: string; code: string; notes: string; isBillable: boolean;
    startTime: string; endTime: string; duration: number;
  } | null>(null);

  // ─── Reference data ─────────────────────────────────────────────────────────
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activityCodes, setActivityCodes] = useState<ActivityCode[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.getAccounts(), api.getActivityCodes()])
      .then(([accts, codes]) => {
        setAccounts(accts);
        setActivityCodes(codes);
      })
      .catch(() => {});
  }, [user]);

  // ─── Form state ─────────────────────────────────────────────────────────────
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [duration, setDuration] = useState(0);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [isBillable, setIsBillable] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // Project picker filter
  const [includeClosedProjects, setIncludeClosedProjects] = useState(false);

  // Quick-add project
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [savingProject, setSavingProject] = useState(false);

  // ─── Timer state ────────────────────────────────────────────────────────────
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [timerStart, setTimerStart] = useState<number | null>(null);
  const [timerStartTimestamp, setTimerStartTimestamp] = useState<string | null>(null);
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now());
  // Stopped but not yet saved: the "ready to save" banner shows until Save or Discard.
  const [timerStopped, setTimerStopped] = useState(false);
  // One timer on hold while another runs (frozen; comes back paused on save/discard).
  const [heldTimer, setHeldTimer] = useState<HeldTimer | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const guardrailRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const shownWarningsRef = useRef<Set<string>>(new Set());
  // Prevents the auto-calc useEffect from overwriting the duration that handleStopTimer
  // already computed from elapsed seconds (which is authoritative after a timer stop).
  const timerJustStoppedRef = useRef(false);

  // Auto-calculate duration when start + end time are both set manually (not from running timer)
  useEffect(() => {
    if (!startTime || !endTime || timerRunning || timerPaused) return;
    // Skip once after handleStopTimer — that function already set the correct duration
    // from elapsed seconds. Without this guard, the effect fires and re-calculates from
    // the HH:mm strings, which returns 24h when start == end (same minute) or when
    // the strings differ from the actual elapsed time.
    if (timerJustStoppedRef.current) {
      timerJustStoppedRef.current = false;
      return;
    }
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    let diffMinutes = (eh * 60 + em) - (sh * 60 + sm);
    if (diffMinutes <= 0) diffMinutes += 1440; // midnight crossing
    setDuration(diffMinutes / 60);
  }, [startTime, endTime, timerRunning, timerPaused]);

  // ─── localStorage persistence ────────────────────────────────────────────────
  // Snapshot of this tab's timer in persisted shape (null when idle). Read through a
  // ref by the cross-tab `storage` listener so it never sees a stale closure.
  const persistedRef = useRef<PersistedTimerState | null>(null);
  const editingIdRef = useRef<string | null>(null);
  editingIdRef.current = editingId;
  // While editing an older entry the form holds THAT entry, so persist the timer's
  // own fields (stashed in preEditStateRef) instead of the edit's.
  const timerForm = editingId && preEditStateRef.current
    ? preEditStateRef.current
    : { account: selectedAccount, project: selectedProject, code: selectedCode, notes, isBillable, startTime, endTime, duration };
  persistedRef.current = hasPersistableTimer({ timerRunning, timerPaused, timerStopped, held: heldTimer })
    ? {
        timerRunning,
        timerPaused,
        timerStart,
        accumulatedSeconds,
        lastActivityTime,
        timerStartTimestamp,
        startTime: timerForm.startTime,
        savedAt: Date.now(),
        date: todayStr,
        // Persist form fields so they survive a page refresh
        selectedAccount: timerForm.account,
        selectedProject: timerForm.project,
        selectedCode: timerForm.code,
        notes: timerForm.notes,
        isBillable: timerForm.isBillable,
        timerStopped,
        endTime: timerStopped ? timerForm.endTime : "",
        duration: timerStopped ? timerForm.duration : 0,
        held: heldTimer,
      }
    : null;

  const saveTimerState = useCallback(() => {
    if (persistedRef.current) {
      localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify(persistedRef.current));
    } else {
      localStorage.removeItem(TIMER_STORAGE_KEY);
    }
  }, [timerRunning, timerPaused, timerStart, accumulatedSeconds, lastActivityTime, timerStartTimestamp, startTime, todayStr, selectedAccount, selectedProject, selectedCode, notes, isBillable, timerStopped, endTime, duration, heldTimer, editingId]);

  // Tell every other open tab this timer was stopped (ENG-116). The `storage` event
  // only fires in OTHER tabs, so this tab never reacts to its own broadcast.
  const broadcastTimerStop = () => {
    try { localStorage.setItem(TIMER_STOP_SIGNAL_KEY, String(Date.now())); } catch { /* storage unavailable */ }
  };

  // Restore on mount
  useEffect(() => {
    const saved = localStorage.getItem(TIMER_STORAGE_KEY);
    if (!saved) return;
    try {
      const state = JSON.parse(saved);
      if (state.date !== format(new Date(), "yyyy-MM-dd")) {
        localStorage.removeItem(TIMER_STORAGE_KEY);
        return;
      }
      setTimerPaused(state.timerPaused);
      setAccumulatedSeconds(state.accumulatedSeconds);
      setLastActivityTime(state.lastActivityTime || Date.now());
      if (state.timerStartTimestamp) setTimerStartTimestamp(state.timerStartTimestamp);
      if (state.startTime) setStartTime(state.startTime);
      // Restore form fields
      if (state.selectedAccount) setSelectedAccount(state.selectedAccount);
      if (state.selectedProject) setSelectedProject(state.selectedProject);
      if (state.selectedCode) setSelectedCode(state.selectedCode);
      if (state.notes) setNotes(state.notes);
      if (typeof state.isBillable === "boolean") setIsBillable(state.isBillable);
      if (state.timerRunning && state.timerStart) {
        setTimerStart(state.timerStart);
        setTimerRunning(true);
      }
      if (state.timerStopped) {
        timerJustStoppedRef.current = true; // keep the stopped duration as saved
        setTimerStopped(true);
        setEndTime(state.endTime || "");
        setDuration(state.duration || 0);
      }
      if (state.held) setHeldTimer(state.held);
    } catch {
      localStorage.removeItem(TIMER_STORAGE_KEY);
    }
  }, []);

  // Last change received from another tab. Its echo is never written back (see
  // shouldSkipEcho); the marker clears once this tab changes the timer itself.
  const remoteMarkerRef = useRef<RemoteMarker>(null);
  useEffect(() => {
    if (remoteMarkerRef.current) {
      if (shouldSkipEcho(remoteMarkerRef.current, persistedRef.current)) return;
      remoteMarkerRef.current = null;
    }
    saveTimerState();
  }, [saveTimerState]);

  // Cross-tab sync (ENG-116): stop when another tab stops, adopt a timer another tab
  // started or paused. Without this, a stale tab kept ticking and rewrote the stopped
  // timer back into storage, so the next new tab resurrected it.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      const action = timerSyncAction(e.key, e.newValue, persistedRef.current, format(new Date(), "yyyy-MM-dd"));
      if (action.type !== "ignore") {
        remoteMarkerRef.current = action.type === "adopt" ? { kind: "adopt", state: action.state } : { kind: "stop" };
      }
      if (action.type === "stop") {
        // The main timer ended elsewhere (saved, discarded or stopped). Any timer on
        // hold, or a stopped entry waiting to save, arrives in the state write after.
        setTimerRunning(false);
        setTimerPaused(false);
        setTimerStopped(false);
        setElapsedSeconds(0);
        setAccumulatedSeconds(0);
        setTimerStart(null);
        setTimerStartTimestamp(null);
        shownWarningsRef.current.clear();
        if (editingIdRef.current) { preEditStateRef.current = null; return; }
        setStartTime("");
        setEndTime("");
        setDuration(0);
        setSelectedAccount("");
        setSelectedProject("");
        setSelectedCode("");
        setNotes("");
      } else if (action.type === "adopt") {
        const st = action.state;
        setTimerPaused(st.timerPaused);
        setAccumulatedSeconds(st.accumulatedSeconds);
        setElapsedSeconds(st.accumulatedSeconds);
        setTimerStart(st.timerRunning ? st.timerStart : null);
        setTimerRunning(st.timerRunning && !!st.timerStart);
        setTimerStartTimestamp(st.timerStartTimestamp ?? null);
        setTimerStopped(!!st.timerStopped);
        setHeldTimer(st.held ?? null);
        setLastActivityTime(st.lastActivityTime || Date.now());
        const form = {
          account: st.selectedAccount ?? "",
          project: st.selectedProject ?? "",
          code: st.selectedCode ?? "",
          notes: st.notes ?? "",
          isBillable: typeof st.isBillable === "boolean" ? st.isBillable : true,
          startTime: st.startTime ?? "",
          endTime: st.timerStopped ? st.endTime ?? "" : "",
          duration: st.timerStopped ? st.duration ?? 0 : 0,
        };
        // A tab mid-edit of an existing entry keeps its form; the timer's fields wait
        // in preEditStateRef and come back when the edit is saved or cancelled.
        if (editingIdRef.current) { preEditStateRef.current = form; return; }
        if (st.timerStopped) timerJustStoppedRef.current = true;
        setStartTime(form.startTime);
        setEndTime(form.endTime);
        setDuration(form.duration);
        setSelectedAccount(form.account);
        setSelectedProject(form.project);
        setSelectedCode(form.code);
        setNotes(form.notes);
        setIsBillable(form.isBillable);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Tick
  useEffect(() => {
    if (timerRunning && timerStart) {
      timerRef.current = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - timerStart) / 1000) + accumulatedSeconds);
      }, 1000);
    } else if (timerPaused) {
      setElapsedSeconds(accumulatedSeconds);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timerRunning, timerStart, timerPaused, accumulatedSeconds]);

  // Guardrails (8h limit + inactivity warning)
  useEffect(() => {
    if (!timerRunning) {
      if (guardrailRef.current) clearInterval(guardrailRef.current);
      return;
    }
    guardrailRef.current = setInterval(() => {
      const now = Date.now();
      const currentElapsed = timerStart
        ? Math.floor((now - timerStart) / 1000) + accumulatedSeconds
        : accumulatedSeconds;

      if (format(new Date(), "yyyy-MM-dd") !== todayStr) {
        handleAutoSave("It's a new day!");
        return;
      }
      if ((now - lastActivityTime) / 60000 >= 60 && !shownWarningsRef.current.has("inactivity")) {
        shownWarningsRef.current.add("inactivity");
        toast({ title: "Timer still running", description: "Your timer has been running for a while." });
      }
      if (currentElapsed / 3600 >= 8) handleAutoSave("8-hour limit reached.");
    }, 30000);
    return () => { if (guardrailRef.current) clearInterval(guardrailRef.current); };
  }, [timerRunning, timerStart, accumulatedSeconds, lastActivityTime, todayStr, heldTimer]);

  // Track user activity
  useEffect(() => {
    const update = () => setLastActivityTime(Date.now());
    window.addEventListener("click", update);
    window.addEventListener("keydown", update);
    return () => {
      window.removeEventListener("click", update);
      window.removeEventListener("keydown", update);
    };
  }, []);

  // ─── Timer handlers ──────────────────────────────────────────────────────────
  const handleStartTimer = () => {
    const now = new Date();
    setTimerStart(now.getTime());
    setTimerStartTimestamp(now.toISOString());
    setStartTime(format(now, "HH:mm"));
    setAccumulatedSeconds(0);
    setElapsedSeconds(0);
    setTimerRunning(true);
    setTimerPaused(false);
    setTimerStopped(false);
    setLastActivityTime(now.getTime());
    shownWarningsRef.current.clear();
  };

  const handlePauseTimer = () => {
    if (timerStart) {
      const elapsed = Math.floor((Date.now() - timerStart) / 1000) + accumulatedSeconds;
      setAccumulatedSeconds(elapsed);
      setElapsedSeconds(elapsed);
    }
    setTimerRunning(false);
    setTimerPaused(true);
    setTimerStart(null);
  };

  const handleResumeTimer = () => {
    setTimerStart(Date.now());
    setTimerRunning(true);
    setTimerPaused(false);
    setLastActivityTime(Date.now());
  };

  const handleStopTimer = () => {
    const now = new Date();
    timerJustStoppedRef.current = true; // prevent auto-calc effect from overriding elapsed-based duration
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerStopped(true); // shows the "ready to save" banner until Save or Discard
    setEndTime(format(now, "HH:mm"));
    const elapsed = timerStart
      ? Math.floor((now.getTime() - timerStart) / 1000) + accumulatedSeconds
      : accumulatedSeconds;
    setDuration(Math.max(1, Math.round(elapsed / 60)) / 60);
    setElapsedSeconds(0);
    setAccumulatedSeconds(0);
    setTimerStart(null);
    shownWarningsRef.current.clear();
    localStorage.removeItem(TIMER_STORAGE_KEY);
    broadcastTimerStop();
  };

  // Bring the timer on hold back into the main slot, paused (user clicks Resume).
  const restoreHeldTimer = (h: HeldTimer) => {
    setHeldTimer(null);
    setTimerRunning(false);
    setTimerStopped(false);
    setTimerPaused(true);
    setTimerStart(null);
    setAccumulatedSeconds(h.accumulatedSeconds);
    setElapsedSeconds(h.accumulatedSeconds);
    setTimerStartTimestamp(h.timerStartTimestamp);
    setStartTime(h.startTime);
    setEndTime("");
    setDuration(0);
    setSelectedAccount(h.selectedAccount);
    setSelectedProject(h.selectedProject);
    setSelectedCode(h.selectedCode);
    setNotes(h.notes);
    setIsBillable(h.isBillable);
    if (h.selectedAccount) {
      api.getProjects({ accountId: h.selectedAccount, includeAll: includeClosedProjects }).then(setProjects).catch(() => setProjects([]));
    } else {
      setProjects([]);
    }
  };

  const handleResetTimer = () => {
    const hadTimer = timerRunning || timerPaused || timerStopped;
    setTimerRunning(false);
    setTimerPaused(false);
    setTimerStopped(false);
    setElapsedSeconds(0);
    setAccumulatedSeconds(0);
    setTimerStart(null);
    setTimerStartTimestamp(null);
    setStartTime("");
    setEndTime("");
    shownWarningsRef.current.clear();
    localStorage.removeItem(TIMER_STORAGE_KEY);
    if (hadTimer) broadcastTimerStop();
    // Saving or discarding the new timer brings the held one back (paused).
    if (heldTimer) restoreHeldTimer(heldTimer);
  };

  // Hold & start new: freeze the current timer (with its client, code and notes) and
  // start a fresh one with a blank form, e.g. to time a phone call for another client.
  const canHold = canHoldTimer({ timerRunning, timerPaused, held: heldTimer, editingId });
  const handleHoldAndStartNew = () => {
    if (!canHold) return;
    setHeldTimer(buildHeldTimer({
      timerStart, accumulatedSeconds, timerStartTimestamp, startTime,
      selectedAccount, selectedProject, selectedCode, notes, isBillable,
    }, Date.now()));
    setSelectedAccount("");
    setSelectedProject("");
    setSelectedCode("");
    setNotes("");
    setIsBillable(true);
    setProjects([]);
    setDuration(0);
    setEndTime("");
    handleStartTimer();
    toast({ title: "Timer on hold", description: "Started a new timer. The held one comes back when you save or discard this one." });
  };

  // Discard a stopped, unsaved entry (the banner's Discard button).
  const handleDiscardStopped = () => {
    setDuration(0);
    setNotes("");
    setSelectedCode("");
    setSelectedProject("");
    setSelectedAccount("");
    setProjects([]);
    handleResetTimer();
  };

  const handleAutoSave = async (reason: string) => {
    const now = new Date();
    const elapsed = timerStart
      ? Math.floor((now.getTime() - timerStart) / 1000) + accumulatedSeconds
      : accumulatedSeconds;
    const totalMinutes = Math.max(1, Math.round(elapsed / 60));
    const autoEndTime = format(now, "HH:mm");

    setTimerRunning(false);
    setTimerPaused(false);
    setElapsedSeconds(0);
    setAccumulatedSeconds(0);
    setTimerStart(null);
    setEndTime(autoEndTime);
    shownWarningsRef.current.clear();
    localStorage.removeItem(TIMER_STORAGE_KEY);
    broadcastTimerStop();

    if (!selectedAccount || selectedAccount === "none" || !selectedCode) {
      setTimerStopped(true);
      setDuration(totalMinutes / 60);
      toast({ title: "Timer stopped", description: `${reason} Please fill in the form and save manually.`, variant: "destructive" });
      return;
    }

    try {
      const startISO = timerStartTimestamp || (startTime ? `${todayStr}T${startTime}:00` : null);
      await api.createTimeEntry({
        entry_date: todayStr,
        account_id: selectedAccount || null,
        project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
        activity_code: selectedCode,
        minutes: totalMinutes,
        start_time: startISO,
        end_time: `${todayStr}T${autoEndTime}:00`,
        notes: notes || undefined,
        billable: isBillable,
      });
      setLastSavedAt(Date.now());
      setDuration(0);
      setStartTime("");
      setEndTime("");
      setTimerStartTimestamp(null);
      setNotes("");
      setSelectedCode("");
      setSelectedProject("");
      if (heldTimer) restoreHeldTimer(heldTimer);
      toast({ title: "Timer auto-saved", description: reason });
    } catch (error: any) {
      setTimerStopped(true);
      setDuration(totalMinutes / 60);
      toast({ title: "Auto-save failed", description: error.message, variant: "destructive" });
    }
  };

  const formatTimer = (seconds: number) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, "0");
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // ─── Form handlers ───────────────────────────────────────────────────────────
  const handleAccountChange = (accountId: string) => {
    setSelectedAccount(accountId);
    setSelectedProject("");
    if (accountId && accountId !== "none") {
      api.getProjects({ accountId, includeAll: includeClosedProjects }).then(setProjects).catch(() => setProjects([]));
    } else {
      setProjects([]);
    }
  };

  // Re-fetch when the closed-projects toggle changes and an account is already selected
  useEffect(() => {
    if (!selectedAccount || selectedAccount === "none") return;
    api.getProjects({ accountId: selectedAccount, includeAll: includeClosedProjects }).then(setProjects).catch(() => {});
  }, [includeClosedProjects]);

  const handleCodeChange = (code: string) => {
    setSelectedCode(code);
    const ac = activityCodes.find((c) => c.code === code);
    if (ac) {
      setIsBillable(ac.billable_default);
      if (["pto", "holiday"].includes(ac.category)) setIsBillable(false);
    }
  };

  const handleQuickAddProject = async () => {
    if (!newProjectTitle.trim() || !selectedAccount) {
      toast({ title: "Project title is required", variant: "destructive" });
      return;
    }
    setSavingProject(true);
    try {
      const newProject = await api.createProject({
        title: newProjectTitle.trim(),
        account_id: selectedAccount,
        description: newProjectDescription || null,
        status: "open",
      });
      const updated = await api.getProjects({ accountId: selectedAccount, includeAll: includeClosedProjects });
      setProjects(updated);
      setSelectedProject(newProject.id);
      setNewProjectTitle("");
      setNewProjectDescription("");
      setShowAddProjectModal(false);
      toast({ title: "Project created" });
    } catch (error: any) {
      toast({ title: "Error creating project", description: error.message, variant: "destructive" });
    } finally {
      setSavingProject(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || selectedAccount === "none") {
      toast({ title: "Account is required", variant: "destructive" });
      return;
    }
    if (!selectedCode) {
      toast({ title: "Activity code is required", variant: "destructive" });
      return;
    }
    if (notesRequiredFor(selectedCode, activityCodes) && !notes.trim()) {
      toast({ title: "Notes are required for this activity code", variant: "destructive" });
      return;
    }
    // A running/paused timer keeps its elapsed time in the timer display, not in
    // `duration` — only Stop transfers it. Clicking "Add Entry" while the timer is
    // still going (or before entering a manual duration) would submit minutes=0,
    // which violates the time_entries CHECK (minutes > 0) and fails with a 500 (B6).
    const effectiveMinutes = Math.round(duration * 60);
    if (effectiveMinutes <= 0) {
      toast({
        title: "Enter a duration",
        description: (timerRunning || timerPaused)
          ? 'Click Stop to log the timer’s elapsed time, or use "Add a different entry" below for a separate entry.'
          : "Duration must be greater than zero.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    const localToISO = (timeStr: string) => new Date(`${entryDate}T${timeStr}:00`).toISOString();
    // When editing an existing entry, always use the form's start/end times (not the running timer's timestamp)
    const startISO = editingId
      ? (startTime ? localToISO(startTime) : null)
      : (timerStartTimestamp || (startTime ? localToISO(startTime) : null));
    const endISO = endTime ? localToISO(endTime) : null;

    try {
      if (editingId) {
        await api.updateTimeEntry(editingId, {
          account_id: selectedAccount || null,
          project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
          activity_code: selectedCode,
          minutes: effectiveMinutes,
          start_time: startISO,
          end_time: endISO,
          notes: notes || undefined,
          billable: isBillable,
        });
        toast({ title: "Entry updated" });
      } else {
        await api.createTimeEntry({
          entry_date: entryDate,
          account_id: selectedAccount || null,
          project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
          activity_code: selectedCode,
          minutes: effectiveMinutes,
          start_time: startISO,
          end_time: endISO,
          notes: notes || undefined,
          billable: isBillable,
        });
        toast({ title: "Entry added" });
      }

      setLastSavedAt(Date.now());
      setEditingId(null);

      if (timerRunning || timerPaused || (timerStopped && editingId)) {
        // A timer is still live (or a stopped one waits to save) — restore its form
        // context and keep the sheet open for it.
        restorePreEditForm();
      } else {
        // No timer — full reset and close
        setDuration(0);
        setStartTime("");
        setEndTime("");
        setTimerStartTimestamp(null);
        setNotes("");
        setSelectedCode("");
        setSelectedProject("");
        setSelectedAccount("");
        setProjects([]);
        handleResetTimer();
        setIsSheetOpen(false);
      }
    } catch (error: any) {
      const msg = error.message || "Unknown error";
      const description = msg.includes("closed pay period")
        ? "This entry's pay period is closed and cannot be edited."
        : msg.includes("approved")
        ? "This entry has been approved and cannot be changed."
        : msg;
      toast({ title: "Could not save entry", description, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  // Put the timer's own form back after editing an older entry.
  const restorePreEditForm = () => {
    const saved = preEditStateRef.current;
    preEditStateRef.current = null;
    setEntryDate(todayStr);
    if (saved) {
      if (timerStopped) timerJustStoppedRef.current = true;
      setSelectedAccount(saved.account);
      setSelectedProject(saved.project);
      setSelectedCode(saved.code);
      setNotes(saved.notes);
      setIsBillable(saved.isBillable);
      setStartTime(saved.startTime);
      setEndTime(saved.endTime);
      setDuration(saved.duration);
      if (saved.account) {
        api.getProjects({ accountId: saved.account, includeAll: includeClosedProjects }).then(setProjects).catch(() => setProjects([]));
      }
    } else {
      setDuration(0);
      setEndTime("");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    if (timerRunning || timerPaused || timerStopped) {
      restorePreEditForm();
    } else {
      setDuration(1);
      setStartTime("");
      setEndTime("");
      setNotes("");
      setSelectedCode("");
      setSelectedProject("");
      setProjects([]);
      handleResetTimer();
      setIsSheetOpen(false);
    }
  };

  // ─── Sheet open helpers ──────────────────────────────────────────────────────
  const openSheet = (date?: string) => {
    // A running, paused or stopped-unsaved timer owns the form: reopening the sheet
    // must not wipe its start/end times (that is how a stopped timer got "lost").
    if (timerRunning || timerPaused || timerStopped) {
      setIsSheetOpen(true);
      return;
    }
    setEntryDate(date || format(new Date(), "yyyy-MM-dd"));
    setEditingId(null);
    setStartTime("");
    setEndTime("");
    setIsSheetOpen(true);
  };

  const openSheetWithContext = (accountId: string, projectId: string) => {
    // If a timer is already running, just surface the sheet — do NOT override the
    // form fields. Switching account/project mid-timer would silently attribute
    // the elapsed time to the wrong client and wipe the original start time.
    if (timerRunning || timerPaused || timerStopped) {
      setIsSheetOpen(true);
      toast({
        title: timerStopped ? "Unsaved timer entry" : "Timer already running",
        description: timerStopped
          ? "Save or discard the stopped timer before starting a new one."
          : "Stop and save the current entry, or use Hold & start new.",
      });
      return;
    }
    setEntryDate(format(new Date(), "yyyy-MM-dd"));
    setEditingId(null);
    setStartTime("");
    setEndTime("");
    setSelectedAccount(accountId);
    setSelectedProject(projectId);
    // Load projects for this account into the picker (non-blocking)
    if (accountId) api.getProjects({ accountId, includeAll: includeClosedProjects }).then(setProjects).catch(() => {});
    setIsSheetOpen(true);
  };

  const openSheetForEdit = (entry: TimeEntry, _accountsList: Account[]) => {
    // Save current timer form state so we can restore it after the edit
    if (timerRunning || timerPaused || timerStopped) {
      preEditStateRef.current = {
        account: selectedAccount,
        project: selectedProject,
        code: selectedCode,
        notes,
        isBillable,
        startTime,
        endTime,
        duration,
      };
    }
    setEditingId(entry.id);
    setEntryDate(entry.entry_date);
    setSelectedAccount(entry.account_id || "");
    setSelectedProject(entry.project_id || "");
    setSelectedCode(entry.activity_code);
    setDuration(entry.minutes / 60);
    if (entry.start_time) setStartTime(format(new Date(entry.start_time), "HH:mm"));
    if (entry.end_time) setEndTime(format(new Date(entry.end_time), "HH:mm"));
    setNotes(entry.notes || "");
    setIsBillable(entry.billable);
    if (entry.account_id) {
      api.getProjects({ accountId: entry.account_id, includeAll: includeClosedProjects }).then(setProjects).catch(() => setProjects([]));
    }
    setIsSheetOpen(true);
  };

  const closeSheet = () => {
    setIsSheetOpen(false);
    // If a timer was stopped but the entry hasn't been saved yet (endTime is set),
    // keep the form data in context so the user can reopen and save it. Only do a
    // full reset when the form is truly idle (nothing to preserve).
    if (!timerRunning && !timerPaused && !timerStopped && !heldTimer && !endTime) handleResetTimer();
  };

  return (
    <TimerContext.Provider value={{
      isSheetOpen, openSheet, openSheetForEdit, openSheetWithContext, closeSheet,
      timerRunning, timerPaused, elapsedSeconds,
      handleStartTimer, handlePauseTimer, handleResumeTimer, handleStopTimer, handleResetTimer, formatTimer,
      timerStopped, heldTimer, canHold, handleHoldAndStartNew, handleDiscardStopped,
      entryDate, setEntryDate,
      selectedAccount, setSelectedAccount, handleAccountChange,
      selectedProject, setSelectedProject,
      selectedCode, handleCodeChange,
      duration, setDuration,
      startTime, setStartTime,
      endTime, setEndTime,
      notes, setNotes,
      isBillable, setIsBillable,
      editingId,
      accounts, activityCodes, projects, includeClosedProjects, setIncludeClosedProjects,
      showAddProjectModal, setShowAddProjectModal,
      newProjectTitle, setNewProjectTitle,
      newProjectDescription, setNewProjectDescription,
      handleQuickAddProject, savingProject,
      handleSubmit, handleCancelEdit, saving,
      lastSavedAt,
    }}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimerContext() {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error("useTimerContext must be used within TimerProvider");
  return ctx;
}
